import React, { useCallback, useEffect, useRef, useState } from "react";
import { InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import { useNavigate } from "react-router-dom";

// Game and welcome styles are kept in this topic file.
const mathsInvadersStyles = `
/* General game area */
.game-area {
  position: relative;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at center, #1c1f26 0%, #0c0d12 100%);
  color: #f0f0f0;
  overflow: hidden;
  border: none;
  border-radius: 0;
  box-shadow: none;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}


/* Question area */
.question {
  text-align: center;
  font-size: 20px;
  padding: 12px;
  background: linear-gradient(to right, #444, #222);
  border-bottom: 1px solid #666;
  color: #fff;
  box-shadow: 0 2px 5px rgba(255, 255, 255, 0.05);
}

/* Wrong hit counter */
.wrong-hit-counter {
  text-align: center;
  font-size: 16px;
  padding: 6px;
  color: #ff6666;
}

/* Invader group */
.invaders {
  position: absolute;
  top: 60px;
  width: 100%;
  display: flex;
  justify-content: space-around;
  flex-wrap: nowrap;
}

/* Each invader */
.invader {
  position: relative;
  width: 80px;
  height: 80px;
   background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  color: white;
  font-size: 18px;
  pointer-events: auto;
  user-select: none;
  text-shadow: 0 0 5px black;
}


.invader:hover {
  transform: scale(1.1);
}


/* Bullet */
.bullet {
  position: absolute;
  width: 4px;
  height: 15px;
  background: #33ccff;
  border-radius: 2px;
  transform: translateX(-50%);
  box-shadow: 0 0 6px #33ccff;
}
.overlay-text {
  position: absolute;
  top: 35%;
  width: 100%;
  text-align: center;
  font-size: 4rem;
  font-weight: bold;
  color: white;
  opacity: 0;
  animation: fadeInOut 2s ease-in-out forwards;
  z-index: 10;
  text-shadow: 0 0 20px #fff, 0 0 40px #0ff;
}

.level-complete {
  color: #00ff88;
  text-shadow: 0 0 10px #00ff88, 0 0 20px #00ff88;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: scale(0.95); }
  10% { opacity: 1; transform: scale(1); }
  90% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.05); }
}
.level-announcement {
  position: fixed; /* Use fixed for full-screen centering */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 3rem;
  font-weight: bold;
  color: #ffffff;
  text-shadow: 0 0 10px #00faff, 0 0 20px #00faff;
  animation: fadeInScale 2s ease-out;
  z-index: 9999;
  text-align: center;
  background: rgba(0, 0, 0, 0.75);
  padding: 30px 60px;
  border-radius: 20px;
  border: 2px solid #00faff;
  pointer-events: none; /* So it doesn't block gameplay accidentally */
}

@keyframes fadeInScale {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.05);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

.wrong-hit-indicator {
  display: flex;
  gap: 12px;
  margin: 15px auto;
  justify-content: center;
}

.hit-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: transparent;
  border: 2px solid #ff4d4f;
  box-shadow: 0 0 6px rgba(255, 77, 79, 0.4);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.hit-circle.active {
  background-color: #ff4d4f;
  box-shadow: 0 0 12px 4px rgba(255, 77, 79, 0.8);
}

/* =========================================
   GAME OVER EXPLOSION
========================================= */

.explosion-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  background: #02040a;
}

.explosion-container {
  position: relative;
  width: 350px;
  height: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Bright central explosion */

.explosion-core {
  position: absolute;
  width: 70px;
  height: 70px;
  border-radius: 50%;

  background:
    radial-gradient(
      circle,
      #ffffff 0%,
      #fff200 20%,
      #ff9500 45%,
      #ff3300 70%,
      transparent 75%
    );

  box-shadow:
    0 0 30px #ffffff,
    0 0 60px #ffcc00,
    0 0 100px #ff5500;

  animation: explosionCore 1.2s ease-out forwards;
}

/* Expanding explosion rings */

.explosion-ring {
  position: absolute;
  border-radius: 50%;
  border: 8px solid #ff9500;
  opacity: 0;
}

.explosion-ring-1 {
  width: 80px;
  height: 80px;
  animation: explosionRing 0.8s ease-out forwards;
}

.explosion-ring-2 {
  width: 100px;
  height: 100px;
  animation: explosionRing 0.9s ease-out 0.1s forwards;
}

.explosion-ring-3 {
  width: 120px;
  height: 120px;
  animation: explosionRing 1s ease-out 0.2s forwards;
}

/* Screen flash */

.explosion-flash {
  position: fixed;
  inset: 0;

  background: white;

  opacity: 0;

  pointer-events: none;

  animation: explosionFlash 0.7s ease-out forwards;
}

/* Explosion emoji */

.explosion-text {
  position: absolute;

  font-size: 7rem;

  z-index: 10;

  animation: explosionEmoji 1.2s ease-out forwards;
}


/* =========================================
   EXPLOSION ANIMATIONS
========================================= */

@keyframes explosionCore {

  0% {
    transform: scale(0.1);
    opacity: 0;
  }

  15% {
    transform: scale(1.8);
    opacity: 1;
  }

  55% {
    transform: scale(3.5);
    opacity: 1;
  }

  100% {
    transform: scale(6);
    opacity: 0;
  }
}


@keyframes explosionRing {

  0% {
    transform: scale(0.2);
    opacity: 1;
  }

  100% {
    transform: scale(4);
    opacity: 0;
  }
}


@keyframes explosionFlash {

  0% {
    opacity: 0;
  }

  10% {
    opacity: 0.9;
  }

  25% {
    opacity: 0.25;
  }

  100% {
    opacity: 0;
  }
}


@keyframes explosionEmoji {

  0% {
    transform: scale(0);
    opacity: 0;
  }

  20% {
    transform: scale(1.5) rotate(-10deg);
    opacity: 1;
  }

  50% {
    transform: scale(2.1) rotate(8deg);
    opacity: 1;
  }

  100% {
    transform: scale(3);
    opacity: 0;
  }
}
/* Welcome screen styles */
.welcome-container {
  height: 100vh;
  width: 100%;
  background: linear-gradient(to bottom right, #1a1a1a, #333333);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-family: 'Orbitron', sans-serif;
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
  100% {
    transform: translateY(0px);
  }
}

.welcome-logo {
  width: 400px;
  max-width: 80%;
  margin-bottom: 40px;
  animation: float 4s ease-in-out infinite;
}
.welcome-page {
  position: relative;
  height: 100vh;
  background: radial-gradient(ellipse at bottom, #1a1a1a 0%, #000 100%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.stars, .stars::before, .stars::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 200%;
  background: transparent url('https://raw.githubusercontent.com/ryanparag/starry-night-background/main/stars.png') repeat;
  animation: twinkle 200s linear infinite;
  opacity: 0.7;
  z-index: 0;
}

.stars::before {
  animation-duration: 300s;
  opacity: 0.5;
}

.stars::after {
  animation-duration: 500s;
  opacity: 0.3;
}

@keyframes twinkle {
  from { transform: translateY(0); }
  to { transform: translateY(-100px); }
}

.welcome-content {
  z-index: 1;
  text-align: center;
}



.button-group {
  display: flex;
  gap: 20px;
}

.button-group button {
  padding: 15px 30px;
  font-size: 18px;
  background: white;
  color: black;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.3s ease;
}

.welcome-page .button-group button:hover {
  transform: scale(1.1);
  background: #67e8f9;
  color: #082f49;
  box-shadow: 0 0 20px rgba(34, 211, 238, 0.45);
}
.answer-correct-tick {
  position: absolute;
  right: 14px;
  top: 50%;

  width: 46px;
  height: 46px;

  transform: translateY(-50%);

  pointer-events: none;

  animation: answerTickPop 0.4s
    cubic-bezier(0.34, 1.56, 0.64, 1)
    forwards;
}

.answer-correct-tick svg {
  width: 100%;
  height: 100%;
  display: block;
}

.answer-tick-circle {
  fill: #063d24;

  stroke: #4ade80;
  stroke-width: 4;

  stroke-dasharray: 164;
  stroke-dashoffset: 164;

  animation: answerTickCircleDraw
    0.4s ease-out forwards;
}

.answer-tick-path {
  fill: none;

  stroke: #ffffff;
  stroke-width: 6;

  stroke-linecap: round;
  stroke-linejoin: round;

  stroke-dasharray: 45;
  stroke-dashoffset: 45;

  animation: answerTickDraw
    0.32s ease-out 0.25s forwards;
}

@keyframes answerTickCircleDraw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes answerTickDraw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes answerTickPop {
  0% {
    opacity: 0;
    transform:
      translateY(-50%)
      scale(0.4);
  }

  100% {
    opacity: 1;
    transform:
      translateY(-50%)
      scale(1);
  }
}

/* Cinematic welcome artwork; only the astronaut floats. */
.invaders-welcome.welcome-page{position:relative;width:100%;min-height:100svh;height:100svh;display:flex;align-items:center;justify-content:center;overflow:hidden;background:#020b20 url('/assets/maths-invaders/background.png') center/cover no-repeat;font-family:Arial,sans-serif}
.invaders-welcome-scene{position:relative;width:min(100vw,133.333svh);aspect-ratio:4/3;flex-shrink:0}
.invaders-welcome-brand{position:absolute;top:4.3%;left:25%;width:50%;display:flex;align-items:center;justify-content:center;gap:18px;color:#e7f5ff;letter-spacing:.42em;font-size:clamp(10px,1.35vw,21px);white-space:nowrap;text-shadow:0 0 12px #53c5f7}
.invaders-welcome-brand b{font-weight:400;color:#8feeff}
.invaders-welcome-brand:before,.invaders-welcome-brand:after{content:'';width:76px;height:2px;background:linear-gradient(90deg,transparent,#82eaff);box-shadow:0 0 9px #19a9fb;flex-shrink:1}
.invaders-welcome-brand:after{transform:rotate(180deg)}
.invaders-astronaut{position:absolute;z-index:3;left:0%;top:10%;width:47%;height:64%;object-fit:contain;filter:drop-shadow(0 0 12px #23baff88);animation:invaders-astronaut-float 6s ease-in-out infinite;pointer-events:none;user-select:none}
.invaders-welcome .invaders-welcome-content{position:absolute;inset:0;max-width:none;width:100%;margin:0;transform:none;padding:0;display:block;text-align:center}
.invaders-welcome .invaders-title-image{position:absolute;left:44%;top:26%;width:54%;height:34%;max-width:none;object-fit:contain;margin:0;animation:none;filter:drop-shadow(0 8px 8px #0009);user-select:none;pointer-events:none}
.invaders-welcome .invaders-welcome-buttons{position:absolute;left:21.5%;bottom:13%;width:61%;display:grid;grid-template-columns:1.1fr 1fr;gap:2.2%;align-items:center}
.invaders-welcome .invaders-welcome-buttons button{position:relative;isolation:isolate;display:flex;align-items:center;justify-content:center;gap:clamp(10px,1.6vw,25px);min-width:0;height:clamp(54px,7.5vw,106px);padding:12px 16px;border:0;border-radius:0;margin:0;clip-path:polygon(5% 0,95% 0,100% 18%,100% 82%,95% 100%,5% 100%,0 82%,0 18%);background-color:#061831;background-image:linear-gradient(145deg,#8795b4, #edf8ff 16%,#233a60 18%,#07152b 70%,#03cfff);color:#eefaff;font-family:'Arial Black',Arial,sans-serif;font-size:clamp(13px,2.3vw,34px);font-weight:900;letter-spacing:.015em;line-height:1;cursor:pointer;box-shadow:none;transform:none;filter:drop-shadow(0 0 5px #13c9fd77);transition:background-color .22s,filter .22s,color .22s}
.invaders-welcome-buttons button:before{content:'';position:absolute;inset:3px;background:linear-gradient(145deg,#132b4d,#04152d 68%,#0a3151);clip-path:inherit;z-index:-2}
.invaders-welcome-buttons button:after{content:'';position:absolute;inset:7px;border:1px solid #3be5ff66;clip-path:inherit;z-index:-1;background:linear-gradient(130deg,#4ca8c822,transparent 55%,#035c7c22)}
.invaders-welcome .invaders-welcome-buttons button:hover,.invaders-welcome .invaders-welcome-buttons button:focus-visible{background-color:#0984bd;background-image:linear-gradient(145deg,#c3f9ff,#f4ffff 14%,#00dfff 19%,#20edff 80%,#e6ffff 95%);color:#fff;transform:none;filter:drop-shadow(0 0 16px #00dbff);text-shadow:0 0 8px #c1f8ff}
.invaders-welcome-buttons button:hover:before,.invaders-welcome-buttons button:focus-visible:before{background:linear-gradient(145deg,#05b5f5,#096ba6 40%,#043c70 65%,#0b9fd2)}
.invaders-welcome-buttons button:hover:after,.invaders-welcome-buttons button:focus-visible:after{border:2px solid #75f8ff;background:linear-gradient(125deg,#50e6ff55,transparent 48%,#01ceff44);box-shadow:inset 0 0 20px #28e8ff}
.invaders-welcome-buttons button:focus-visible{outline:3px solid white;outline-offset:-10px}
.invaders-play-icon{color:#b4fbff;font-size:1.3em;text-shadow:0 0 10px #06dfff;line-height:1}
.invaders-info-icon{display:grid;place-items:center;flex-shrink:0;border:3px solid currentColor;border-radius:50%;width:1.4em;height:1.4em;font-family:Georgia,serif;font-size:1.05em;font-style:normal;line-height:1}
@keyframes invaders-astronaut-float{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-18px) rotate(1deg)}}
@media(max-aspect-ratio:3/4){.invaders-welcome-scene{width:100vw;height:100svh;aspect-ratio:auto}.invaders-welcome-brand{left:8%;width:84%;top:6%;font-size:12px;gap:12px;letter-spacing:.3em}.invaders-astronaut{left:0%;top:15%;width:58%;height:43%}.invaders-welcome .invaders-title-image{left:33%;top:30%;width:67%;height:26%}.invaders-welcome .invaders-welcome-buttons{left:14%;width:72%;bottom:12%;grid-template-columns:1fr;gap:18px}.invaders-welcome .invaders-welcome-buttons button{height:65px;font-size:21px}}
@media(prefers-reduced-motion:reduce){.invaders-astronaut{animation:none}.invaders-welcome .invaders-welcome-buttons button{transition:none}}

/* Gameplay HUD shared visually across all three levels. */
.game-area.invaders-play-field{background:#030c20 url('/assets/maths-invaders/game-background.png') center/cover no-repeat;isolation:isolate}
.invaders-game-frame{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:6;filter:drop-shadow(0 0 5px #00cfff55)}
.invaders-game-brand{position:absolute;top:12px;left:2.5%;width:clamp(120px,20vw,240px);display:flex;flex-direction:column;align-items:center;gap:3px;z-index:7;pointer-events:none}
.invaders-game-brand img{display:block;width:100%;height:clamp(42px,5.6vw,82px);object-fit:contain}
.invaders-game-brand span{font-size:clamp(11px,1.15vw,17px);font-weight:700;letter-spacing:.35em;color:#49eeff;text-shadow:0 0 10px #06c4ef}
.invaders-play-field .question{position:relative;isolation:isolate;z-index:7;width:56%;max-width:900px;min-height:72px;margin:9px auto 0;padding:16px 24px;background:#16cce9;border:0;box-shadow:0 0 16px #00bfe755;color:#eefaff;clip-path:polygon(3% 0,97% 0,100% 18%,100% 82%,97% 100%,3% 100%,0 82%,0 18%);display:flex;align-items:center;justify-content:center;font-family:'Segoe UI',Arial,sans-serif}
.invaders-play-field .question:before{content:'';position:absolute;inset:2px;z-index:-1;clip-path:inherit;background:linear-gradient(90deg,#103b5a44,transparent 40%,#103b5a44),linear-gradient(#1e90b711 1px,transparent 1px),linear-gradient(90deg,#1e90b711 1px,transparent 1px),#03142b;background-size:auto,16px 16px,16px 16px,auto;box-shadow:inset 0 0 24px #0ec5ff22}
.invaders-play-field .question .katex{font-size:1em;white-space:normal}.invaders-question-lines{font-size:clamp(14px,1.3vw,19px);line-height:1.45;width:100%;font-weight:500}.invaders-question-lines p{margin:2px 0}
.invaders-play-field .wrong-hit-indicator{position:relative;z-index:7;gap:20px;margin:16px auto 0;pointer-events:none}
.invaders-play-field .hit-circle{width:clamp(20px,2.3vw,32px);height:clamp(20px,2.3vw,32px);border:2px solid #ff575a;box-shadow:0 0 9px #ff282a88,inset 0 0 6px #ef1c1c33}
.invaders-play-field .hit-circle.active{background:#ff3f4d;box-shadow:0 0 15px #ff2026bb}
.invaders-play-field .invaders{top:clamp(90px,13vh,145px)}
.invaders-game-pause{position:absolute;top:22px;right:2.5%;width:54px;height:58px;z-index:10001;display:grid;place-items:center;padding:8px;background:linear-gradient(145deg,#183750,#031224);border:2px solid #bcecff;clip-path:polygon(24% 0,76% 0,100% 24%,100% 76%,76% 100%,24% 100%,0 76%,0 24%);color:#e4faff;cursor:pointer;filter:drop-shadow(0 0 5px #2ac5ff77);box-shadow:inset 0 0 12px #17b8f744}
.invaders-game-pause svg{width:100%;height:100%;fill:currentColor;filter:drop-shadow(0 0 3px #00c8ff)}.invaders-game-pause:hover{background:#07628c;color:white}.invaders-game-pause:focus-visible{outline:3px solid #fff;outline-offset:-6px}
@media(max-width:800px){.invaders-game-brand{left:2%;top:12px;width:18%}.invaders-game-brand img{height:43px}.invaders-game-brand span{font-size:10px;letter-spacing:.2em}.invaders-play-field .question{width:60%;padding:12px 16px;min-height:66px;font-size:clamp(15px,2.5vw,22px)!important}.invaders-game-pause{right:2%;top:20px;width:42px;height:46px}.invaders-play-field .wrong-hit-indicator{margin-top:12px;gap:16px}}
@media(max-width:500px){.invaders-game-brand{width:112px;left:10px;top:10px}.invaders-game-pause{top:16px;right:12px}.invaders-play-field .question{margin-top:86px;width:calc(100% - 24px);min-height:58px;padding:12px 15px;font-size:18px!important}.invaders-play-field .invaders{top:185px}.invaders-play-field .wrong-hit-indicator{margin-top:10px}.invaders-question-lines{font-size:14px}}

.invaders-play-field .question .katex .text{font-size:inherit!important}
/* Reference-matched question frame: cyan chamfered corners and dark grid. */
.invaders-play-field .question{width:42%;max-width:660px;min-height:88px;padding:20px 26px;background:transparent;clip-path:none;box-shadow:none;filter:drop-shadow(0 0 6px #00bce766)}
.invaders-play-field .question:before{inset:5px;clip-path:polygon(2.5% 0,97.5% 0,100% 19%,100% 81%,97.5% 100%,2.5% 100%,0 81%,0 19%);box-shadow:inset 0 0 28px #0e9eff22}
.invaders-play-field .question>.invaders-question-frame{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:0;overflow:visible}
.invaders-play-field .question>.katex,.invaders-play-field .question>.invaders-question-lines{position:relative;z-index:1}
.invaders-play-field .question .katex{font-size:clamp(20px,2.05vw,32px)!important;text-shadow:0 0 4px #a3eaff44}
@media(max-width:1000px){.invaders-play-field .question{width:56%;min-height:78px;padding:17px 23px}.invaders-play-field .question .katex{font-size:21px!important}}
@media(max-width:500px){.invaders-play-field .question{width:calc(100% - 24px);min-height:70px;padding:15px 22px}.invaders-play-field .question .katex{font-size:19px!important}}

.invaders-welcome .invaders-topic{position:absolute;left:44%;top:60%;width:54%;margin:10px 0 0;padding:8px 14px;border-radius:8px;background:linear-gradient(90deg,transparent,#020817e8 18%,#020817e8 82%,transparent);box-shadow:0 8px 22px #0005;color:#f1fbff;font-size:clamp(13px,1.2vw,18px);font-weight:500;letter-spacing:.035em;line-height:1.5;text-align:center;text-shadow:0 2px 3px #000,0 0 8px #000,0 0 16px #000}
.invaders-topic strong{color:#68eaff;font-weight:700}
@media(max-aspect-ratio:3/4){.invaders-welcome .invaders-topic{left:25%;top:54%;width:73%;margin-top:8px;font-size:13px;letter-spacing:0}}
`;

/* =========================================================
   LEVEL 1
   EASY COMPLEX NUMBER EQUATIONS
========================================================= */

const level1Questions = [
  {
    questionLatex:
      "\\text{Solve for }z:\\quad z + (2+i) = 5+4i",
    correctAnswer: "3+3i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad z - (1+2i) = 4+i",
    correctAnswer: "5+3i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad z + 3i = 2+7i",
    correctAnswer: "2+4i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad z - 2 = 4+5i",
    correctAnswer: "6+5i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad z + (4-i) = 7+2i",
    correctAnswer: "3+3i",
  },
];

/* =========================================================
   LEVEL 2
   INTERMEDIATE COMPLEX NUMBER EQUATIONS
========================================================= */

const level2Questions = [
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 2z = 6+8i",
    correctAnswer: "3+4i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 3z = 9-6i",
    correctAnswer: "3-2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 2z + 1 = 7+6i",
    correctAnswer: "3+3i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 2z - 3i = 8+5i",
    correctAnswer: "4+4i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 3z + 2 = 11+9i",
    correctAnswer: "3+3i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad iz = -4+3i",
    correctAnswer: "3+4i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad iz = 2+5i",
    correctAnswer: "5-2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 2iz = -6+4i",
    correctAnswer: "2+3i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 4z - 2 = 10+8i",
    correctAnswer: "3+2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 5z + 5i = 10+20i",
    correctAnswer: "2+3i",
  },
];

/* =========================================================
   LEVEL 3
   HARDER COMPLEX NUMBER EQUATIONS
   FINAL LEVEL
========================================================= */

const level3Questions = [
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (1+i)z = 4+2i",
    correctAnswer: "3-i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (1-i)z = 2+4i",
    correctAnswer: "-1+3i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (2+i)z = 5+5i",
    correctAnswer: "3+i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (2-i)z = 7+i",
    correctAnswer: "3+2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (1+2i)z = -3+4i",
    correctAnswer: "1+2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 2(z+1+i)=8+6i",
    correctAnswer: "3+2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad 3(z-1+i)=6+9i",
    correctAnswer: "3+2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (1+i)z + 2 = 6+4i",
    correctAnswer: "4",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (1-i)z + i = 5-2i",
    correctAnswer: "4+i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (2+i)z - 1 = 6+4i",
    correctAnswer: "3+i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (2-i)z + 2i = 8+i",
    correctAnswer: "3+i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (3+i)z = 7+i",
    correctAnswer: "2-i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (1+2i)z = 5",
    correctAnswer: "1-2i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (2+2i)z = 4+8i",
    correctAnswer: "3+i",
  },
  {
    questionLatex:
      "\\text{Solve for }z:\\quad (3-i)z = 8+6i",
    correctAnswer: "2+2i",
  },
];

/* =========================================================
   HELPERS
========================================================= */

const shuffleArray = (arr) => {
  return [...arr].sort(() => Math.random() - 0.5);
};

/* =========================================================
   WELCOME SCREEN
========================================================= */

function WelcomeScreen({ onStart }) {
  useEffect(() => { const background = new Image(); background.src = "/assets/maths-invaders/game-background.png"; }, []);
  return (
    <div className="welcome-page invaders-welcome">
      <div className="invaders-welcome-scene">
        <div className="invaders-welcome-brand"><span>LEARN WITH <b>RYAN</b></span></div>
        <img className="invaders-astronaut" src="/assets/maths-invaders/astronaut.png" alt="A smiling astronaut floating in space" draggable="false" />
        <div className="welcome-content invaders-welcome-content">
          <img className="welcome-logo invaders-title-image" src="/assets/maths-invaders/logo.png" alt="Maths Invaders" draggable="false" />
          <p className="invaders-topic"><strong>Topic:</strong> Complex Number Equations</p>
          <div className="button-group invaders-welcome-buttons">
            <button type="button" onClick={onStart}><span className="invaders-play-icon" aria-hidden="true">▶</span><span>START GAME</span></button>
            <button type="button" onClick={() => alert("Controls:\nLeft / Right arrows to move\nHold SPACE to fire\nP to pause\n\nSolve the question and hit the invader displaying the correct answer. Each incorrect hit costs one life.")}><span className="invaders-info-icon" aria-hidden="true">i</span><span>HOW TO PLAY</span></button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* =========================================================
   MAIN GAME
========================================================= */

function InvadersGameHud({ level, isPaused, onPause }) {
  return <>
    <svg className="invaders-game-frame" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
      <defs><linearGradient id="invaders-frame-metal" x2="1" y2="1"><stop stopColor="#b2c4df"/><stop offset=".3" stopColor="#22334e"/><stop offset=".55" stopColor="#728aa8"/><stop offset="1" stopColor="#101b30"/></linearGradient></defs>
      <g fill="none" stroke="url(#invaders-frame-metal)" strokeWidth="7" vectorEffect="non-scaling-stroke"><path d="M2 110V36L35 3H223L260 39H302M698 39H740L777 3H965L998 36V110M2 890V964L35 997H223L260 961H302M698 961H740L777 997H965L998 964V890"/></g>
      <g fill="none" stroke="#89b3d6" strokeWidth="1.2" vectorEffect="non-scaling-stroke"><path d="M10 105V42L42 11H211L249 47H292M708 47H751L789 11H958L990 42V105M10 895V958L42 989H211L249 953H292M708 953H751L789 989H958L990 958V895"/></g>
      <g fill="none" stroke="#15ddff" strokeWidth="2" vectorEffect="non-scaling-stroke"><path d="M10 105V58M990 58V105M10 895V942M990 895V942M42 962H92M908 962H958M270 17H289M711 17H730"/></g>
    </svg>
    <div className="invaders-game-brand"><img src="/assets/maths-invaders/logo.png" alt="Maths Invaders"/><span>LEVEL {level}</span></div>
    <button type="button" className="invaders-game-pause" onKeyDown={event => { if (event.code === "Space" && !isPaused) event.preventDefault(); }} onClick={onPause} aria-label={isPaused ? 'Resume game' : 'Pause game'} title={isPaused ? 'Resume game (P)' : 'Pause game (P)'}><svg viewBox="0 0 40 40" aria-hidden="true">{isPaused ? <path d="M13 8L31 20L13 32Z"/> : <><path d="M11 8H17V32H11Z"/><path d="M23 8H29V32H23Z"/></>}</svg></button>
  </>;
}

function Game() {
  const questionBank = {
    1: level1Questions,
    2: level2Questions,
    3: level3Questions,
  };

  /* =======================================================
     STATE
  ======================================================= */

  const [gameStarted, setGameStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);

  const [level, setLevel] = useState(1);

  const [showLevelText, setShowLevelText] = useState(true);
  const [showLevelCompleteText, setShowLevelCompleteText] =
    useState(false);

  const [gameOverReason, setGameOverReason] = useState(null);
  const [showExplosion, setShowExplosion] = useState(false);

  const [invaderTop, setInvaderTop] = useState(50);

  const [wrongHits, setWrongHits] = useState(0);
  const [correctHits, setCorrectHits] = useState(0);

  const [invaderDirection, setInvaderDirection] = useState(1);

  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionsQueue, setQuestionsQueue] = useState([]);

  const [invaders, setInvaders] = useState([]);

  const [movingDirection, setMovingDirection] = useState(null);
  const [shooterPosition, setShooterPosition] = useState(50);

  const [bullets, setBulletsState] = useState([]);
  const bulletsRef = useRef([]);
  const setBullets = useCallback(next => {
    bulletsRef.current = next;
    setBulletsState(next);
  }, []);

  /* =======================================================
     REFS
  ======================================================= */

  // Reuse one audio context and play only when a new bullet is actually fired.
  const shootingAudioRef = useRef(null);
  const playShootingSound = useCallback(async () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      let context = shootingAudioRef.current;
      if (!context || context.state === 'closed') {
        context = new AudioContext(); shootingAudioRef.current = context;
      }
      if (context.state === 'suspended') await context.resume();
      if (context.state !== 'running') return;
      const now = context.currentTime, oscillator = context.createOscillator();
      const filter = context.createBiquadFilter(), gain = context.createGain();
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(1150, now);
      oscillator.frequency.exponentialRampToValueAtTime(150, now + .14);
      filter.type = 'lowpass'; filter.frequency.value = 1800;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(.12, now + .005);
      gain.gain.exponentialRampToValueAtTime(.001, now + .16);
      oscillator.connect(filter); filter.connect(gain); gain.connect(context.destination);
      oscillator.onended = () => { oscillator.disconnect(); filter.disconnect(); gain.disconnect(); };
      oscillator.start(now); oscillator.stop(now + .17);
    } catch (error) { console.warn('Shooting sound unavailable:', error); }
  }, []);
  useEffect(() => () => {
    const context = shootingAudioRef.current;
    shootingAudioRef.current = null;
    if (context && context.state !== 'closed') void context.close();
  }, []);

  const shooterRef = useRef(null);
  const gameAreaRef = useRef(null);

  const correctAlienSoundRef = useRef(null);
  const wrongAlienSoundRef = useRef(null);

  const invaderRefs = useRef({});
  const invadersRef = useRef([]);

  const levelCompletionTriggeredRef = useRef(false);
  const gameOverTriggeredRef = useRef(false);

  useEffect(() => {
    correctAlienSoundRef.current =
      new Audio("/assets/correctalien.mp3");

    wrongAlienSoundRef.current =
      new Audio("/assets/wrongalien.mp3");

    correctAlienSoundRef.current.preload = "auto";
    wrongAlienSoundRef.current.preload = "auto";

    correctAlienSoundRef.current.volume = 0.4;
    wrongAlienSoundRef.current.volume = 0.4;
  }, []);

  const fireIntervalRef = useRef(null);

  const spaceHeldRef = useRef(false);

  /* =======================================================
     FIRING HELPERS
  ======================================================= */

  const stopFiring = () => {
    if (fireIntervalRef.current) {
      clearInterval(fireIntervalRef.current);

      fireIntervalRef.current = null;
    }
  };

  /* =======================================================
     CREATE AND POSITION ALIENS
  ======================================================= */

  const createPositionedAliens = (
    lvl,
    questionsForLevel
  ) => {
    const answerAliens = shuffleArray(
      questionsForLevel.map((question, index) => ({
        id: `level-${lvl}-answer-${index}`,
        text: question.correctAnswer,
      }))
    );

    /* =========================
       LEVEL 1
    ========================= */

    if (lvl === 1) {
      const positions = [
        10,
        30,
        50,
        70,
        90,
      ];

      return answerAliens.map((answer, index) => ({
        ...answer,
        left: positions[index],
        rowOffset: 0,
      }));
    }

    /* =========================
       LEVEL 2
    ========================= */

    if (lvl === 2) {
      const level2Positions = [
        {
          left: 5,
          rowOffset: 0,
          row: 1,
          direction: 1,
        },
        {
          left: 22,
          rowOffset: 0,
          row: 1,
          direction: 1,
        },
        {
          left: 39,
          rowOffset: 0,
          row: 1,
          direction: 1,
        },
        {
          left: 56,
          rowOffset: 0,
          row: 1,
          direction: 1,
        },
        {
          left: 73,
          rowOffset: 0,
          row: 1,
          direction: 1,
        },

        {
          left: 17,
          rowOffset: 120,
          row: 2,
          direction: -1,
        },
        {
          left: 34,
          rowOffset: 120,
          row: 2,
          direction: -1,
        },
        {
          left: 51,
          rowOffset: 120,
          row: 2,
          direction: -1,
        },
        {
          left: 68,
          rowOffset: 120,
          row: 2,
          direction: -1,
        },
        {
          left: 85,
          rowOffset: 120,
          row: 2,
          direction: -1,
        },
      ];

      return answerAliens.map((answer, index) => ({
        ...answer,

        left:
          level2Positions[index]
            .left,

        rowOffset:
          level2Positions[index]
            .rowOffset,

        row:
          level2Positions[index]
            .row,

        direction:
          level2Positions[index]
            .direction,
      }));
    }

    /* =========================
       LEVEL 3
    ========================= */

    const level3Positions = [
      {
        left: 5,
        rowOffset: 0,
        row: 1,
        direction: 1,
      },
      {
        left: 22,
        rowOffset: 0,
        row: 1,
        direction: 1,
      },
      {
        left: 39,
        rowOffset: 0,
        row: 1,
        direction: 1,
      },
      {
        left: 56,
        rowOffset: 0,
        row: 1,
        direction: 1,
      },
      {
        left: 73,
        rowOffset: 0,
        row: 1,
        direction: 1,
      },

      {
        left: 15,
        rowOffset: 130,
        row: 2,
        direction: -1,
      },
      {
        left: 32,
        rowOffset: 130,
        row: 2,
        direction: -1,
      },
      {
        left: 49,
        rowOffset: 130,
        row: 2,
        direction: -1,
      },
      {
        left: 66,
        rowOffset: 130,
        row: 2,
        direction: -1,
      },
      {
        left: 83,
        rowOffset: 130,
        row: 2,
        direction: -1,
      },

      {
        left: 5,
        rowOffset: 260,
        row: 3,
        direction: 1,
      },
      {
        left: 22,
        rowOffset: 260,
        row: 3,
        direction: 1,
      },
      {
        left: 39,
        rowOffset: 260,
        row: 3,
        direction: 1,
      },
      {
        left: 56,
        rowOffset: 260,
        row: 3,
        direction: 1,
      },
      {
        left: 73,
        rowOffset: 260,
        row: 3,
        direction: 1,
      },
    ];

    return answerAliens.map((answer, index) => ({
      ...answer,

      left:
        level3Positions[index]
          .left,

      rowOffset:
        level3Positions[index]
          .rowOffset,

      row:
        level3Positions[index]
          .row,

      direction:
        level3Positions[index]
          .direction,
    }));
  };

  /* =======================================================
     LOAD LEVEL
  ======================================================= */

  const loadLevel = (lvl) => {
    const questionsForLevel =
      questionBank[lvl];

    if (
      !questionsForLevel ||
      questionsForLevel.length === 0
    ) {
      return;
    }

    const shuffledQuestions =
      shuffleArray(
        questionsForLevel
      );

    const positionedAliens =
      createPositionedAliens(
        lvl,
        questionsForLevel
      );

    setCurrentQuestion(
      shuffledQuestions[0]
    );

    setQuestionsQueue(
      shuffledQuestions.slice(1)
    );

    setInvaders(
      positionedAliens
    );

    invadersRef.current =
      positionedAliens;

    invaderRefs.current = {};

    setWrongHits(0);
    setCorrectHits(0);

    setInvaderTop(50);
    setInvaderDirection(1);

    stopFiring();

    spaceHeldRef.current = false;

    setBullets([]);
    setMovingDirection(null);

    setGameOverReason(null);
    setShowExplosion(false);

    levelCompletionTriggeredRef.current =
      false;

    gameOverTriggeredRef.current =
      false;
  };

  /* =======================================================
     LOAD LEVEL WHEN LEVEL CHANGES
  ======================================================= */

  useEffect(() => {
    loadLevel(level);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  /* =======================================================
     LEVEL ANNOUNCEMENT
  ======================================================= */

  useEffect(() => {
    setShowLevelText(true);

    setGameStarted(false);

    const timer =
      setTimeout(() => {
        setShowLevelText(false);

        setGameStarted(true);
      }, 2000);

    return () =>
      clearTimeout(timer);

  }, [level]);

  /* =======================================================
     COMPLETE CURRENT LEVEL
  ======================================================= */

  const completeCurrentLevel = () => {
    if (
      levelCompletionTriggeredRef.current
    ) {
      return;
    }

    levelCompletionTriggeredRef.current =
      true;

    stopFiring();

    spaceHeldRef.current =
      false;

    setGameStarted(false);
    setMovingDirection(null);
    setBullets([]);

    setShowLevelCompleteText(true);

    const completedLevel =
      level;

    setTimeout(() => {
      setShowLevelCompleteText(false);

      if (
        completedLevel === 3
      ) {
        setGameComplete(true);
      } else {
        setLevel(
          completedLevel + 1
        );
      }

    }, 2000);
  };

  /* =======================================================
     GAME OVER
  ======================================================= */

  const triggerGameOver = (reason) => {
    if (
      gameOverTriggeredRef.current
    ) {
      return;
    }

    gameOverTriggeredRef.current =
      true;

    stopFiring();

    spaceHeldRef.current =
      false;

    setGameStarted(false);
    setMovingDirection(null);
    setBullets([]);

    setShowExplosion(true);

    setTimeout(() => {
      setShowExplosion(false);

      setCurrentQuestion(null);

      setInvaders([]);

      invadersRef.current = [];
      invaderRefs.current = {};

      setGameOverReason(
        reason
      );

    }, 1200);
  };

  /* =======================================================
     INVADER MOVEMENT
  ======================================================= */

  useEffect(() => {
    let animationFrameId;

    const moveInvaders = () => {
      if (
        !gameStarted ||
        isPaused
      ) {
        animationFrameId =
          requestAnimationFrame(
            moveInvaders
          );

        return;
      }

      const currentInvaders =
        invadersRef.current;

      const INVADER_WIDTH_PERCENT =
        10;

      /* =========================
         LEVEL 1
      ========================= */

      if (level === 1) {
        let newDirection =
          invaderDirection;

        const willHitLeft =
          currentInvaders.some(
            (invader) =>
              invader.left <= 0
          );

        const willHitRight =
          currentInvaders.some(
            (invader) =>
              invader.left >=
              100 -
              INVADER_WIDTH_PERCENT
          );

        if (
          willHitLeft ||
          willHitRight
        ) {
          newDirection =
            -invaderDirection;

          setInvaderTop(
            (previous) =>
              previous + 10
          );
        }

        if (
          newDirection !==
          invaderDirection
        ) {
          setInvaderDirection(
            newDirection
          );
        }

        invadersRef.current =
          currentInvaders.map(
            (invader) => {
              const speed =
                0.07;

              let newLeft =
                invader.left +
                newDirection *
                speed;

              newLeft =
                Math.max(
                  0,
                  Math.min(
                    100 -
                    INVADER_WIDTH_PERCENT,
                    newLeft
                  )
                );

              return {
                ...invader,
                left:
                  newLeft,
              };
            }
          );
      }

      /* =========================
         LEVEL 2
      ========================= */

      else if (
        level === 2
      ) {
        const topRow =
          currentInvaders.filter(
            (invader) =>
              invader.row === 1
          );

        const bottomRow =
          currentInvaders.filter(
            (invader) =>
              invader.row === 2
          );

        const topDirection =
          topRow[0]?.direction ??
          1;

        const bottomDirection =
          bottomRow[0]?.direction ??
          -1;

        const topHitEdge =
          topRow.some(
            (invader) =>
              (
                topDirection === 1 &&
                invader.left >=
                100 -
                INVADER_WIDTH_PERCENT
              )
              ||
              (
                topDirection === -1 &&
                invader.left <= 0
              )
          );

        const bottomHitEdge =
          bottomRow.some(
            (invader) =>
              (
                bottomDirection === 1 &&
                invader.left >=
                100 -
                INVADER_WIDTH_PERCENT
              )
              ||
              (
                bottomDirection === -1 &&
                invader.left <= 0
              )
          );

        const newTopDirection =
          topHitEdge
            ? -topDirection
            : topDirection;

        const newBottomDirection =
          bottomHitEdge
            ? -bottomDirection
            : bottomDirection;

        invadersRef.current =
          currentInvaders.map(
            (invader) => {
              const direction =
                invader.row === 1
                  ? newTopDirection
                  : newBottomDirection;

              const speed =
                0.07;

              let newLeft =
                invader.left +
                direction *
                speed;

              newLeft =
                Math.max(
                  0,

                  Math.min(
                    100 -
                    INVADER_WIDTH_PERCENT,

                    newLeft
                  )
                );

              return {
                ...invader,

                left:
                  newLeft,

                direction,
              };
            }
          );

        if (
          topHitEdge ||
          bottomHitEdge
        ) {
          setInvaderTop(
            (previous) =>
              previous + 10
          );
        }
      }

      /* =========================
         LEVEL 3
      ========================= */

      else if (
        level === 3
      ) {
        const rowSpeeds = {
          1: 0.007,
          2: 0.03,
          3: 0.005,
        };

        const rows =
          [1, 2, 3];

        const newDirections =
          {};

        rows.forEach(
          (rowNumber) => {
            const rowInvaders =
              currentInvaders.filter(
                (invader) =>
                  invader.row ===
                  rowNumber
              );

            if (
              rowInvaders.length ===
              0
            ) {
              return;
            }

            const currentDirection =
              rowInvaders[0]
                ?.direction
              ??
              (
                rowNumber === 2
                  ? -1
                  : 1
              );

            const hitEdge =
              rowInvaders.some(
                (invader) =>
                  (
                    currentDirection ===
                    1
                    &&
                    invader.left >=
                    100 -
                    INVADER_WIDTH_PERCENT
                  )
                  ||
                  (
                    currentDirection ===
                    -1
                    &&
                    invader.left <= 0
                  )
              );

            newDirections[
              rowNumber
            ] =
              hitEdge
                ? -currentDirection
                : currentDirection;
          }
        );

        invadersRef.current =
          currentInvaders.map(
            (invader) => {
              const direction =
                newDirections[
                  invader.row
                ]
                ??
                invader.direction
                ??
                1;

              const speed =
                rowSpeeds[
                  invader.row
                ]
                ??
                0.01;

              let newLeft =
                invader.left +
                direction *
                speed;

              newLeft =
                Math.max(
                  0,

                  Math.min(
                    100 -
                    INVADER_WIDTH_PERCENT,

                    newLeft
                  )
                );

              return {
                ...invader,

                left:
                  newLeft,

                direction,
              };
            }
          );

        const anyRowHitEdge =
          rows.some(
            (rowNumber) => {
              const rowInvaders =
                currentInvaders.filter(
                  (invader) =>
                    invader.row ===
                    rowNumber
                );

              if (
                rowInvaders.length ===
                0
              ) {
                return false;
              }

              const direction =
                rowInvaders[0]
                  ?.direction
                ??
                1;

              return rowInvaders.some(
                (invader) =>
                  (
                    direction === 1
                    &&
                    invader.left >=
                    100 -
                    INVADER_WIDTH_PERCENT
                  )
                  ||
                  (
                    direction === -1
                    &&
                    invader.left <= 0
                  )
              );
            }
          );

        if (
          anyRowHitEdge
        ) {
          setInvaderTop(
            (previous) =>
              previous + 10
          );
        }
      }

      setInvaders([
        ...invadersRef.current,
      ]);

      /* =========================
         INVADER HITS SHOOTER
      ========================= */

      if (
        shooterRef.current &&
        gameAreaRef.current
      ) {
        const shooterBox =
          shooterRef.current
            .getBoundingClientRect();

        const gameBox =
          gameAreaRef.current
            .getBoundingClientRect();

        const shooterTop =
          shooterBox.top -
          gameBox.top;

        const shooterBottom =
          shooterTop +
          shooterBox.height;

        const shooterLeft =
          shooterBox.left -
          gameBox.left;

        const shooterRight =
          shooterLeft +
          shooterBox.width;

        for (
          const invader
          of invadersRef.current
        ) {
          const invaderElement =
            invaderRefs.current[
              invader.id
            ];

          if (!invaderElement) {
            continue;
          }

          const invaderBox =
            invaderElement
              .getBoundingClientRect();

          const invaderTopPosition =
            invaderBox.top -
            gameBox.top;

          const invaderBottom =
            invaderTopPosition +
            invaderBox.height;

          const invaderLeft =
            invaderBox.left -
            gameBox.left;

          const invaderRight =
            invaderLeft +
            invaderBox.width;

          const collision =
            invaderRight >
              shooterLeft
            &&
            invaderLeft <
              shooterRight
            &&
            invaderBottom >
              shooterTop
            &&
            invaderTopPosition <
              shooterBottom;

          if (collision) {
            triggerGameOver(
              "hit"
            );

            return;
          }
        }
      }

      animationFrameId =
        requestAnimationFrame(
          moveInvaders
        );
    };

    animationFrameId =
      requestAnimationFrame(
        moveInvaders
      );

    return () =>
      cancelAnimationFrame(
        animationFrameId
      );

  }, [
    invaderDirection,
    gameStarted,
    isPaused,
    level,
  ]);

  /* =======================================================
     KEYBOARD CONTROLS
  ======================================================= */

  useEffect(() => {

    const fireBullet = () => {

      if (
        !gameStarted ||
        isPaused ||
        !shooterRef.current ||
        !gameAreaRef.current
      ) {
        return;
      }

      if (bulletsRef.current.length > 0) {
        return;
      }

      const shooterRect =
        shooterRef.current.getBoundingClientRect();

      const gameRect =
        gameAreaRef.current.getBoundingClientRect();

      const shooterTop =
        shooterRect.top - gameRect.top;

      void playShootingSound();
      setBullets([
        {
          id: Date.now(),
          x: shooterPosition,
          top: shooterTop,
        },
      ]);
    };

    const handleKeyDown = (event) => {

      if (
        event.key.toLowerCase() === "p"
      ) {

        if (event.repeat) {
          return;
        }

        setIsPaused(
          (previous) => !previous
        );

        setMovingDirection(null);

        return;
      }

      if (
        !gameStarted ||
        isPaused
      ) {
        return;
      }

      if (
        event.key === "ArrowLeft"
      ) {

        setMovingDirection("left");

      } else if (
        event.key === "ArrowRight"
      ) {

        setMovingDirection("right");

      } else if (
        event.code === "Space"
      ) {

        if (event.repeat) {
          return;
        }

        fireBullet();
      }
    };

    const handleKeyUp = (event) => {

      if (
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight"
      ) {

        setMovingDirection(null);
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    window.addEventListener(
      "keyup",
      handleKeyUp
    );

    return () => {

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

      window.removeEventListener(
        "keyup",
        handleKeyUp
      );
    };

  }, [
    shooterPosition,
    gameStarted,
    isPaused,
    bullets.length,
    playShootingSound,
  ]);

  /* =======================================================
     SHOOTER MOVEMENT
  ======================================================= */

  useEffect(() => {
    let animationFrameId;

    const moveShooter = () => {
      setShooterPosition(
        (previous) => {
          if (
            movingDirection ===
            "left"
          ) {
            return Math.max(
              previous - 0.5,
              0
            );
          }

          if (
            movingDirection ===
            "right"
          ) {
            return Math.min(
              previous + 0.5,
              100
            );
          }

          return previous;
        }
      );

      animationFrameId =
        requestAnimationFrame(
          moveShooter
        );
    };

    if (
      movingDirection &&
      !isPaused
    ) {
      animationFrameId =
        requestAnimationFrame(
          moveShooter
        );
    }

    return () =>
      cancelAnimationFrame(
        animationFrameId
      );

  }, [
    movingDirection,
    isPaused,
  ]);

  /* =======================================================
     BULLET MOVEMENT + COLLISION
  ======================================================= */

  useEffect(() => {
    let animationFrameId;

    const advanceBullets = () => {
      if (!gameStarted || isPaused) return;
      const survivingBullets = [];

      for (const bullet of bulletsRef.current) {
        const oldTop = bullet.top;
        const newTop = oldTop - 10;
        let hit = null;

        for (const invader of invadersRef.current) {
          const element = invaderRefs.current[invader.id];
          if (!element || !gameAreaRef.current) continue;
          const gameRect = gameAreaRef.current.getBoundingClientRect();
          const alienRect = element.getBoundingClientRect();
          const bulletX = bullet.x / 100 * gameRect.width;
          const alienLeft = alienRect.left - gameRect.left;
          const alienTop = alienRect.top - gameRect.top;
          if (bulletX + 3 > alienLeft && bulletX - 3 < alienLeft + alienRect.width &&
              Math.max(oldTop + 15, newTop + 15) > alienTop &&
              Math.min(oldTop, newTop) < alienTop + alienRect.height) {
            hit = invader;
            break;
          }
        }

        if (hit) {
          // Consume the bullet before applying any hit effects. React state
          // updaters remain pure, including when StrictMode replays them.
          setBullets([]);
          const correct = hit.text === currentQuestion?.correctAnswer;
          const sound = correct ? correctAlienSoundRef.current : wrongAlienSoundRef.current;
          if (sound) { sound.currentTime = 0; sound.play().catch(() => {}); }

          if (correct) {
            const remaining = invadersRef.current.filter(invader => invader.id !== hit.id);
            invadersRef.current = remaining;
            setInvaders(remaining);
            setCorrectHits(previous => previous + 1);
            if (remaining.length === 0) {
              setCurrentQuestion(null);
              setQuestionsQueue([]);
              setGameStarted(false);
              setTimeout(completeCurrentLevel, 0);
            } else if (questionsQueue.length > 0) {
              setCurrentQuestion(questionsQueue[0]);
              setQuestionsQueue(questionsQueue.slice(1));
            }
          } else {
            setWrongHits(previous => previous + 1);
            if (wrongHits >= 2) triggerGameOver('wrong');
          }
          return;
        }

        if (newTop > -20) survivingBullets.push({ ...bullet, top: newTop });
      }
      setBullets(survivingBullets);
    };

    const moveBullets = () => {
      advanceBullets();
      animationFrameId = requestAnimationFrame(moveBullets);
    };
    animationFrameId = requestAnimationFrame(moveBullets);
    return () => cancelAnimationFrame(animationFrameId);
    // Level completion and game-over functions read this effect's game state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, isPaused, currentQuestion, questionsQueue, level, wrongHits, setBullets]);

  /* =======================================================
     EXPLOSION
  ======================================================= */

  if (
    showExplosion
  ) {
    return (
      <div className="game-area explosion-screen">
        <div className="explosion-container">
          <div className="explosion-ring explosion-ring-1" />
          <div className="explosion-ring explosion-ring-2" />
          <div className="explosion-ring explosion-ring-3" />

          <div className="explosion-core" />

          <div className="explosion-flash" />

          <div className="explosion-text">
            💥
          </div>
        </div>
      </div>
    );
  }

  /* =======================================================
     GAME COMPLETE
  ======================================================= */

  if (
    gameComplete
  ) {
    return (
      <div
        className="game-area"
        style={{
          display:
            "flex",

          justifyContent:
            "center",

          alignItems:
            "center",

          flexDirection:
            "column",

          textAlign:
            "center",
        }}
      >
        <h1
          style={{
            fontSize:
              "4rem",

            marginBottom:
              "20px",
          }}
        >
          🏆 YOU WIN!
        </h1>

        <h2>
          Complex Numbers Invaders Complete!
        </h2>

        <p
          style={{
            fontSize:
              "1.3rem",
          }}
        >
          You defeated all 3 levels!
        </p>
      </div>
    );
  }

  /* =======================================================
     GAME OVER
  ======================================================= */

  if (
    gameOverReason === "wrong"
    ||
    gameOverReason === "hit"
  ) {
    return (
      <div
        className="game-area"
        style={{
          display:
            "flex",

          justifyContent:
            "center",

          alignItems:
            "center",

          flexDirection:
            "column",

          textAlign:
            "center",

          overflow:
            "hidden",
        }}
      >
        <div
          style={{
            position:
              "absolute",

            width:
              "500px",

            height:
              "500px",

            borderRadius:
              "50%",

            background:
              "radial-gradient(circle, rgba(0,255,255,0.18) 0%, rgba(0,100,255,0.08) 40%, transparent 70%)",

            filter:
              "blur(10px)",

            pointerEvents:
              "none",
          }}
        />

        <div
          style={{
            position:
              "relative",

            zIndex:
              2,

            padding:
              "50px 70px",

            borderRadius:
              "28px",

            border:
              "2px solid #00faff",

            background:
              "linear-gradient(145deg, rgba(8,12,30,0.96), rgba(15,25,55,0.96))",

            boxShadow:
              "0 0 25px rgba(0,250,255,0.7), 0 0 60px rgba(0,100,255,0.35)",

            maxWidth:
              "700px",
          }}
        >
          <div
            style={{
              fontSize:
                "5rem",

              fontWeight:
                "900",

              color:
                "#ffffff",

              marginBottom:
                "15px",

              letterSpacing:
                "4px",

              textShadow:
                "0 0 10px #00faff, 0 0 25px #00faff, 0 0 45px #0077ff",
            }}
          >
            GAME OVER
          </div>

          <div
            style={{
              fontSize:
                "1.8rem",

              fontWeight:
                "700",

              color:
                "#ff6b6b",

              marginBottom:
                "35px",

              textShadow:
                "0 0 10px rgba(255,80,80,0.6)",
            }}
          >
            {
              gameOverReason ===
              "hit"

                ? "You've been hit by an invader."

                : "Too many incorrect shots!"
            }
          </div>

          <button
            onClick={() =>
              window.location.reload()
            }

            style={{
              padding:
                "16px 34px",

              fontSize:
                "1.25rem",

              fontWeight:
                "700",

              color:
                "#06121f",

              background:
                "linear-gradient(90deg, #00faff, #38bdf8)",

              border:
                "none",

              borderRadius:
                "14px",

              cursor:
                "pointer",

              boxShadow:
                "0 0 18px rgba(0,250,255,0.7)",

              transition:
                "transform 0.2s ease, box-shadow 0.2s ease",
            }}

            onMouseEnter={(event) => {
              event.currentTarget
                .style
                .transform =
                "scale(1.06)";

              event.currentTarget
                .style
                .boxShadow =
                "0 0 28px rgba(0,250,255,1)";
            }}

            onMouseLeave={(event) => {
              event.currentTarget
                .style
                .transform =
                "scale(1)";

              event.currentTarget
                .style
                .boxShadow =
                "0 0 18px rgba(0,250,255,0.7)";
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  /* =======================================================
     MAIN GAME SCREEN
  ======================================================= */

  return (
    <>
      {/* LEVEL ANNOUNCEMENT */}

      {showLevelText && (
        <div className="level-announcement">
          <h1>
            Level {level}
          </h1>
        </div>
      )}

      {/* LEVEL COMPLETE */}

      {showLevelCompleteText && (
        <div className="level-announcement">
          <h1>
            Level {level} Complete!
          </h1>
        </div>
      )}

      {/* PAUSED */}

      {isPaused && (
        <div className="level-announcement">
          <h1>
            PAUSED
          </h1>

          <div
            style={{
              fontSize:
                "1.2rem",

              marginTop:
                "10px",
            }}
          >
            Press P to continue
          </div>
        </div>
      )}

      <div
        className="game-area invaders-play-field"
        data-level={level}
        ref={gameAreaRef}
      >
        <InvadersGameHud level={level} isPaused={isPaused} onPause={() => { stopFiring(); spaceHeldRef.current = false; setMovingDirection(null); setIsPaused(previous => !previous); }} />
        {/* QUESTION */}

        {currentQuestion && (
          <>
            <div
              className="question"

              style={{
                fontSize:
                  "24px",
              }}
            >
              <svg className="invaders-question-frame" viewBox="0 0 600 90" preserveAspectRatio="none" aria-hidden="true">
                <path d="M21 4H579L596 20V70L579 86H21L4 70V20Z" fill="none" stroke="#1adfff" strokeWidth="1.8"/>
                <path d="M23 4H39M561 4H577M23 86H39M561 86H577M4 23V34M4 56V67M596 23V34M596 56V67" fill="none" stroke="#78f6ff" strokeWidth="3"/>
                <path d="M46 5H72M528 5H554M46 85H72M528 85H554M0 40V50M600 40V50" fill="none" stroke="#00a9ed" strokeWidth="2"/>
              </svg>

              <InlineMath
                math={
                  currentQuestion
                    .questionLatex
                }
              />
            </div>

            <div className="wrong-hit-indicator">
              {[0, 1, 2].map(
                (index) => (
                  <div
                    key={
                      index
                    }

                  className={
  `hit-circle ${
    wrongHits > index
      ? "active"
      : ""
  }`
}
                  />
                )
              )}
            </div>
          </>
        )}

        {/* INVADERS */}

        <div className="invaders">
          {invaders.map(
            (invader) => (
              <div
                key={
                  invader.id
                }

                className="invader"

                ref={(element) => {
                  if (element) {
                    invaderRefs.current[
                      invader.id
                    ] =
                      element;
                  } else {
                    delete invaderRefs.current[
                      invader.id
                    ];
                  }
                }}

                style={{
                  left:
                    `${invader.left}%`,

                  top:
                    `${
                      invaderTop +
                      (
                        invader.rowOffset ||
                        0
                      )
                    }px`,

                  position:
                    "absolute",

                  textAlign:
                    "center",

                  backgroundImage:
                    level === 1

                      ? `url('https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/Red%20invader.png?alt=media&token=28f9bae7-04f3-42df-b9fa-99aaa072b578')`

                      : level === 2

                      ? `url('/assets/Alien_Level3.png')`

                      : `url('/assets/Alien_Level4.png')`,

                  backgroundSize:
                    "contain",

                  backgroundRepeat:
                    "no-repeat",

                  backgroundPosition:
                    "center",

                  width:
                    level === 3
                      ? "120px"
                      : level === 2
                      ? "105px"
                      : "90px",

                  height:
                    level === 3
                      ? "120px"
                      : level === 2
                      ? "100px"
                      : "90px",

                  display:
                    "flex",

                  justifyContent:
                    "center",

                  alignItems:
                    "center",
                }}
              >
                <div
                  style={{
                    color:
                      level === 1
                        ? "white"
                        : "#044e99",

                    fontSize:
                      level === 3
                        ? "15px"
                        : level === 2
                        ? "15px"
                        : "17px",

                    fontWeight:
                      "bold",

                    textShadow:
                      level === 1
                        ? "0 0 5px black"
                        : "none",

                    whiteSpace:
                      "nowrap",
                  }}
                >
                  <InlineMath
                    math={
                      invader.text
                    }
                  />
                </div>
              </div>
            )
          )}
        </div>

        {/* SHOOTER */}

        <img
          ref={shooterRef}

          className="shooter"

          src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/Shooter.png?alt=media&token=e3dbba56-9c9d-4214-9556-77770e207f9c"

          alt="Shooter"

          style={{
            position:
              "absolute",

            bottom:
              "10px",

            left:
              `${shooterPosition}%`,

            transform:
              "translateX(-50%)",

            width:
              "60px",

            height:
              "auto",

            pointerEvents:
              "none",
          }}
        />

        {/* BULLETS */}

        {bullets.map(
          (bullet) => (
            <div
              key={
                bullet.id
              }

              className="bullet"

              style={{
                left:
                  `${bullet.x}%`,

                top:
                  `${bullet.top}px`,
              }}
            />
          )
        )}
      </div>
    </>
  );
}

/* =========================================================
   TOP LEVEL COMPONENT
========================================================= */

function MathsInvadersGame() {
  const [
    screen,
    setScreen,
  ] =
    useState("welcome");

  if (
    screen === "welcome"
  ) {
    return (
      <WelcomeScreen
        onStart={() =>
          setScreen("game")
        }
      />
    );
  }

  return <Game />;
}

/* Original welcome route, preserved alongside the game. */

function LegacyMathsInvadersWelcome() {
  const navigate = useNavigate();
  return <WelcomeScreen onStart={() => navigate('/maths-invaders')} />;
};


export function ComplexNumberEquationsWelcome() {
  return <><style>{mathsInvadersStyles}</style><LegacyMathsInvadersWelcome /></>;
}

export default function ComplexNumberEquations() {
  return <><style>{mathsInvadersStyles}</style><MathsInvadersGame /></>;
}
